import { useEffect, useMemo, useState } from 'react';
import feedKo from './legacy/templates/feed-ko.html?raw';
import feedEn from './legacy/templates/feed-en.html?raw';
import homeKo from './legacy/templates/home-ko.html?raw';
import homeEn from './legacy/templates/home-en.html?raw';
import loginKo from './legacy/templates/login-ko.html?raw';
import loginEn from './legacy/templates/login-en.html?raw';
import signupKo from './legacy/templates/signup-ko.html?raw';
import signupEn from './legacy/templates/signup-en.html?raw';
import termsKo from './legacy/templates/terms-ko.html?raw';
import termsEn from './legacy/templates/terms-en.html?raw';
import { configureDocumentLocale, configureLogin, configureSignupForm, extractTemplate, fetchBootstrap, fetchPublicConfig, hydrateFeedShell, loadAdsense, loadLegacyCore, loadScript, preservePartnerCode, resolveLocale, signalFailure } from './legacy/bootstrap';

type PageKind = 'home'|'login'|'terms'|'signup'|'feed';
type StoreConfig = { googlePlayUrl?: string; appStoreUrl?: string };
type StoreKind = 'google' | 'apple';

function pageKind(pathname: string): PageKind {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/' || p === '/support') return 'home';
  if (p === '/login') return 'login';
  if (p === '/signup/terms') return 'terms';
  if (p === '/signup') return 'signup';
  return 'feed';
}

const templates = {
  ko: { feed: feedKo, home: homeKo, login: loginKo, signup: signupKo, terms: termsKo },
  en: { feed: feedEn, home: homeEn, login: loginEn, signup: signupEn, terms: termsEn },
};

function showStoreUnavailableDialog(store: StoreKind, locale: 'ko' | 'en'): void {
  document.getElementById('eddaba-store-unavailable-dialog')?.remove();

  const previousActive = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const overlay = document.createElement('div');
  overlay.id = 'eddaba-store-unavailable-dialog';
  overlay.setAttribute('role', 'presentation');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'padding:24px',
    'background:rgba(0,0,0,.58)',
    'backdrop-filter:blur(4px)',
    '-webkit-backdrop-filter:blur(4px)',
  ].join(';');

  const dialog = document.createElement('div');
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'eddaba-store-dialog-title');
  dialog.setAttribute('aria-describedby', 'eddaba-store-dialog-message');
  dialog.style.cssText = [
    'width:min(100%,360px)',
    'box-sizing:border-box',
    'padding:22px 20px 18px',
    'border:1px solid rgba(255,255,255,.09)',
    'border-radius:18px',
    'background:#1c1e22',
    'box-shadow:0 20px 60px rgba(0,0,0,.52)',
    'color:#fff',
    'text-align:center',
    'font-family:inherit',
  ].join(';');

  const title = document.createElement('h2');
  title.id = 'eddaba-store-dialog-title';
  title.textContent = locale === 'ko' ? '안내' : 'Notice';
  title.style.cssText = 'margin:0;font-size:20px;font-weight:700;line-height:1.3;color:#fff';

  const message = document.createElement('p');
  message.id = 'eddaba-store-dialog-message';
  const storeName = store === 'google' ? 'Google Play' : 'App Store';
  message.textContent = locale === 'ko' ? `${storeName}는 준비중입니다.` : `${storeName} is coming soon.`;
  message.style.cssText = 'margin:14px 0 20px;font-size:14px;font-weight:400;line-height:1.5;color:#c8c8c8';

  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.textContent = locale === 'ko' ? '확인' : 'OK';
  confirm.style.cssText = [
    'width:100%',
    'min-height:46px',
    'border:0',
    'border-radius:12px',
    'background:#fa1155',
    'color:#fff',
    'font:inherit',
    'font-size:14px',
    'font-weight:700',
    'cursor:pointer',
  ].join(';');

  const close = () => {
    document.removeEventListener('keydown', onKeyDown);
    overlay.remove();
    previousActive?.focus();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close();
  };

  confirm.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });
  document.addEventListener('keydown', onKeyDown);

  dialog.append(title, message, confirm);
  overlay.append(dialog);
  document.body.append(overlay);
  confirm.focus();
}

function configureLandingStoreLinks(config: StoreConfig, locale: 'ko' | 'en'): void {
  const urls: Record<StoreKind, string> = {
    google: String(config.googlePlayUrl || '').trim(),
    apple: String(config.appStoreUrl || '').trim(),
  };

  document.querySelectorAll<HTMLAnchorElement>('a.landing-store-button').forEach((link) => {
    const imageSource = link.querySelector<HTMLImageElement>('.landing-store-mark')?.getAttribute('src') || '';
    const store: StoreKind | null = imageSource.includes('google-play-mark')
      ? 'google'
      : imageSource.includes('apple-mark')
        ? 'apple'
        : null;
    if (!store) return;

    const url = urls[store];
    link.onclick = null;
    link.onkeydown = null;

    if (url) {
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.removeAttribute('role');
      link.removeAttribute('aria-haspopup');
      delete link.dataset.storeUnavailable;
      return;
    }

    link.removeAttribute('href');
    link.removeAttribute('target');
    link.removeAttribute('rel');
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.dataset.storeUnavailable = '1';
    link.onclick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      showStoreUnavailableDialog(store, locale);
    };
    link.onkeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      showStoreUnavailableDialog(store, locale);
    };
  });
}

function installChatThreadPollingGuard(): () => void {
  const originalSetInterval = window.setInterval;
  const nativeSetInterval = originalSetInterval.bind(window);

  window.setInterval = ((handler: TimerHandler, timeout?: number, ...args: any[]) => {
    const callback = typeof handler === 'function' ? handler : null;
    const source = callback ? Function.prototype.toString.call(callback) : '';
    if (!callback || !source.includes('fetchChatThreads')) {
      return nativeSetInterval(handler, timeout, ...args);
    }

    // DM updates normally arrive through WebSocket/FCM. Legacy bundles used a
    // short REST timer as a fallback, which can keep calling getDmThreads even
    // while Home/Profile is visible. Keep only a low-frequency fallback while
    // an actual DM chat screen is in the foreground.
    const guardedHandler = (...callbackArgs: any[]) => {
      if (document.hidden) return;
      const chatVisible = ['chat-main-screen', 'chat-detail-screen', 'chat-detail-menu-screen']
        .some((id) => {
          const element = document.getElementById(id);
          return Boolean(element && !element.hidden);
        });
      if (!chatVisible) return;
      return callback(...callbackArgs);
    };

    return nativeSetInterval(guardedHandler, Math.max(Number(timeout || 0), 60_000), ...args);
  }) as typeof window.setInterval;

  return () => {
    window.setInterval = originalSetInterval;
  };
}

export default function App() {
  const locale = useMemo(() => resolveLocale(), []);
  const kind = pageKind(location.pathname);
  const template = useMemo(() => extractTemplate(templates[locale][kind]), [locale, kind]);
  const [readyHtml] = useState(template.body);

  useEffect(() => {
    configureDocumentLocale(locale);
    document.body.className = template.bodyClass;
    document.title = template.title;
    localStorage.setItem('eddaba.locale', locale);
    preservePartnerCode();

    // The legacy landing template contains fallback search URLs. Disable them
    // immediately and only enable a store when the runtime config supplies a URL.
    if (kind === 'home') configureLandingStoreLinks({}, locale);

    let cancelled = false;
    (async () => {
      try {
        if (kind === 'feed') {
          const data = await fetchBootstrap(locale);
          if (cancelled) return;
          hydrateFeedShell(data, locale);

          const feedRoot = document.querySelector<HTMLElement>('[data-feed-root]');
          if (
            feedRoot?.dataset.phoneVerificationRequired === '1' &&
            feedRoot.dataset.phoneVerified !== '1'
          ) {
            await loadScript('/assets/js/phone-verification.js?v=20260919-octomo-web-layout-1');
            return;
          }

          // Keep a visible safety notice inside actual chat rooms. The first
          // visit is expanded; users may collapse it to a persistent summary.
          await loadScript('/assets/js/chat-safety-notice.js?v=20260918-chat-safety-1');

          // Install the iOS-parity DOM observer before the legacy scripts can
          // open a chat/survival socket. This prevents an initial survival_joined
          // ACK from flashing a false "new participant" message.
          await loadScript('/assets/js/survival-ios-parity.js?v=20260830-chat-myrooms-parity-6');
          await loadScript('/assets/js/survival-self-avatar-fix.js?v=20260830-self-host-me-1');
          const restoreChatThreadPolling = installChatThreadPollingGuard();
          try {
            await loadLegacyCore();
          } finally {
            restoreChatThreadPolling();
          }
          // The legacy top-right button remains the unread-message filter in Chat.
          // In Ideal Survival this layer safely takes over the same button and
          // toggles rooms hosted or joined by the current user.
          await loadScript('/assets/js/chat-survival-myrooms-filter.js?v=20260919-myrooms-icon-3');
          return;
        }
        if (kind === 'login') {
          // Preserve the original behavior: already signed-in users do not stay on login.
          const session = await fetch('/auth/session', { credentials: 'same-origin', cache: 'no-store' }).then(r => r.ok ? r.json() : null).catch(() => null);
          if (session?.result === 'ok' && Number(session?.data?.userId || 0) > 0) {
            location.replace('/home');
            return;
          }
          const cfg = await fetchPublicConfig();
          if (cancelled) return;
          configureLogin(cfg);
          await loadLegacyCore();
          await loadScript('/assets/js/auth.js', true);
          return;
        }
        if (kind === 'signup') {
          configureSignupForm();
          await loadLegacyCore();
          await loadScript('/assets/js/signup.js');
          return;
        }
        if (kind === 'terms') {
          await loadLegacyCore();
          await loadScript('/assets/js/terms.js');
          return;
        }
        await loadLegacyCore();
        if (kind === 'home') {
          if (window.location.pathname === '/support' || window.location.hash === '#support') {
            requestAnimationFrame(() => {
              const el = document.getElementById('support');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            });
          }
          // Store links are runtime configuration. Empty values intentionally
          // stay on this page and open the localized custom "coming soon" dialog.
          // AdSense is limited to the public, publisher-authored landing content.
          void fetchPublicConfig()
            .then((cfg) => {
              if (cancelled) return;
              configureLandingStoreLinks(cfg, locale);
              loadAdsense(cfg.adsenseClientId || '');
            })
            .catch(() => {
              if (!cancelled) configureLandingStoreLinks({}, locale);
            });
        }
      } catch (error) {
        if (!cancelled && error instanceof Error && error.message !== 'session_expired') signalFailure(error.message);
      }
    })();
    return () => {
      cancelled = true;
      document.getElementById('eddaba-store-unavailable-dialog')?.remove();
    };
  }, [kind, locale, template.bodyClass, template.title]);

  return <div id="legacy-app-fragment" dangerouslySetInnerHTML={{ __html: readyHtml }} />;
}
